import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// MUI components
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";

import { Helmet } from "react-helmet-async";

// MUI styles
import { useTheme } from "@mui/material/styles";

// MUI icons
import DoneIcon from "@mui/icons-material/Done";

// API functions
import { confirmWinner, getAuctionResults } from "../../api/auctions";

// Localization
import { useTranslation } from "react-i18next";

// Custom UI components
import MUIConfirm from "../../components/UI/MUIConfirm";
import MUIAlert from "../../components/UI/MUIAlert";

const Results = () => {
  const theme = useTheme();
  const Navigate = useNavigate();
  const { t } = useTranslation();
  const [bids, setBids] = useState([]);
  const [auction, setAuction] = useState([]);
  const [highestBid, setHighestBid] = useState(0);
  const { id } = useParams();
  const [loading, setLoading] = useState(true); // Add loading state
  const [submittingWinner, setSubmittingWinner] = useState(false); // Add loading state

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [confirmParameter, setconfirmParameter] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success"); // Default severity

  const [error, setError] = useState(null);

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  const showConfirm = (title, content, onConfirm, parameter) => {
    setConfirmTitle(title);
    setConfirmContent(content);
    setConfirmCallback(() => onConfirm);
    setconfirmParameter(parameter);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmTitle("");
    setConfirmContent("");
    setConfirmCallback(null);
    setconfirmParameter(null);
  };

  const handleConfirmAction = () => {
    if (confirmCallback && confirmParameter) {
      confirmCallback(confirmParameter);
    }
    handleConfirmClose();
  };

  const handleSelectWinner = (winnerId) => {
    showConfirm(
      t("results_page.confirm_select_title"),
      t("results_page.confirm_select_body"),
      async (winnerId) => {
        setSubmittingWinner(true); // Set loading to true while extending
        try {
          await confirmWinner(id, winnerId);
          showAlert(t("results_page.select_successful"));
          setSubmittingWinner(false); // Set loading to true while extending
          await new Promise((resolve) => setTimeout(resolve, 3000));
          Navigate("/izsoles");
        } catch (error) {
          alert(t("errors.err"));
          setError(t("errors.err"));
          setSubmittingWinner(false);
        }
      },
      winnerId
    );
  };

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const fetchedResults = await getAuctionResults(id);
        setHighestBid(fetchedResults[0].highest_bid);
        setAuction(fetchedResults[0]);
        setBids(fetchedResults[0].all_bids.reverse());
        setLoading(false);

        if (
          fetchedResults[0].status != "completed" ||
          fetchedResults[0].winner_id > 1
        ) {
          Navigate("/izsoles");
        }
      } catch (error) {
        t("errors.err");
        setLoading(false);
      }
    };

    fetchResults();

    const intervalId = setInterval(fetchResults, 10000);

    return () => clearInterval(intervalId);
  }, []);

  if (error) {
    return (
      <div>
        {t("my_auctions.error_message")}: {error}
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t("seo.title.results")}</title>
        <meta property="og:title" content={t("seo.title.results")} />

        <meta name="robots" content="noindex" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <MUIConfirm
          open={confirmOpen}
          onClose={handleConfirmClose}
          title={confirmTitle}
          content={confirmContent}
          onConfirm={handleConfirmAction}
        />

        <MUIAlert
          open={alertOpen}
          onClose={handleAlertClose}
          severity={alertSeverity}
          message={alertMessage}
        />

        <Box sx={{ marginBottom: 4, pt: 4 }}>
          <Box sx={{ position: "relative" }}>
            <Typography variant="h4" gutterBottom>
              {t("results_page.title")}
            </Typography>

            {loading ? ( // Show skeleton for active auctions
              <Paper sx={{ padding: 2, marginBottom: 4 }}>
                <Typography variant="h6" gutterBottom>
                  <Skeleton width="40%" />
                </Typography>
                <List>
                  {Array.from({ length: 2 }).map((_, index) => (
                    <ListItem key={index} sx={{ marginBottom: 1 }}>
                      <ListItemText
                        primary={<Skeleton width="60%" />}
                        secondary={<Skeleton width="40%" />}
                      />
                      <ListItemText
                        primary={<Skeleton width="30%" />}
                        secondary={<Skeleton width="30%" />}
                      />
                      <ListItemSecondaryAction>
                        <Skeleton
                          width="100px"
                          height="40px"
                          style={{ marginRight: 1 }}
                        />
                        <Skeleton
                          width="80px"
                          height="40px"
                          style={{ marginRight: 1 }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ) : bids.length > 0 ? (
              <Paper sx={{ padding: 2, marginBottom: 4 }}>
                <Typography variant="h6" gutterBottom>
                  {t("results_page.subtitle")} #{auction.auction_id}
                </Typography>
                <List>
                  {bids.map((bid, index) => (
                    <>
                      <ListItem
                        button
                        key={bid.bid_id}
                        sx={{
                          marginBottom: 1,
                        }}
                      >
                        <ListItemText
                          primary={`${Number(bid.amount)} €`}
                          secondary={`${bid.first_name} ${bid.last_name}`}
                          sx={{ color: theme.palette.text.primary }}
                        />
                        <ListItemSecondaryAction>
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => handleSelectWinner(bid.user_id)}
                            sx={{
                              marginRight: 1,
                              color: theme.palette.secondary.darkText,
                            }}
                            endIcon={<DoneIcon />}
                          >
                            {submittingWinner ? (
                              <CircularProgress size={24} color="inherit" /> // Show spinner when loading
                            ) : (
                              t("results_page.select_winner")
                            )}
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index !== bids.length - 1 ? <Divider /> : null}
                    </>
                  ))}
                </List>
              </Paper>
            ) : (
              <></>
            )}
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Results;
